import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { styled } from '@linaria/react'
import { MinimalButton, MinimalAlert, MinimalCard } from '@ovasabi/ui-minimal'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { CityPicker, PageHeader, Screen } from '../../components/chow'
import { Button } from '../../components/ui/Button'
import { mediaUrl, uploadImage } from '../../lib/runtime'
import { IMAGE_ACCEPT, type ImageAdvisory } from '../../lib/imageProcessing'
import { useBackNav, useChooseChow, type ChowRole } from './ChooseChowContext'
import { useAuth } from './auth/AuthContext'
import {
  BasketIcon,
  CameraIcon,
  CardIcon,
  ChatIcon,
  ChevronRightIcon,
  CloseIcon,
  type IconProps,
  LifebuoyIcon,
  PinIcon,
  PlateIcon,
  SparkIcon,
  UserIcon,
} from '../../components/icons'
import { useEdition } from '../../edition/EditionProvider'
import { EDITIONS, type Edition } from '../../lib/edition'
import { allowSeedContent, commandPayload, commands, isLiveRuntime } from '../../lib/runtime'
import { useVendorProfileSnapshot } from '../../generated/prototypeRuntime'
import { confirmedUpsert, enumShort } from './chefShared'
import { useUnreadThreadCount } from './unreadMessages'
import { ShareKitchenCard } from './ShareKitchenCard'
import { locationLine, useVendorLocations } from './projections'
import { prototypeStoreRegistry } from '../../generated/prototypeRuntime'
import type { TenantProjectionStore } from '@ovasabi/frontend-kit'
import { MinimalInput } from '@ovasabi/ui-minimal'
import { ChowThumb } from './ChowThumb'
import { saveChefPhoto } from './chefPhoto'
import { PhotoAdvice } from './imagePicker'
import { formatPhone } from '../../lib/phone'
import { InstallAppNudge } from '../notifications/InstallApp'

interface MenuItem {
  to: string
  label: string
  hint: string
  icon: (props: IconProps) => ReactNode
  /** Which role(s) see this row. Absent = both. */
  roles?: readonly ChowRole[]
  group?: string
}

/**
 * Profile menu, resolved per active role. A customer manages orders, plans and
 * wallet; a chef's kitchen management (dispatch, menus, earnings) lives on the
 * dock, so their profile menu leads with earnings + support.
 */
const buildMenu = (isChef: boolean): readonly MenuItem[] => [
  { group: 'Your food', to: '/orders', label: 'Orders', hint: 'Track your orders & status', icon: BasketIcon, roles: ['customer'] },
  { group: 'Your food', to: '/mealplan', label: 'My meal plan', hint: 'Compose your week from any chefs', icon: PlateIcon, roles: ['customer'] },
  { to: '/wallet', label: isChef ? 'Earnings' : 'Wallet', hint: isChef ? 'Payouts & order income' : 'Balance, top-up & transactions', icon: CardIcon },
  {
    to: '/wallet?tab=methods',
    label: isChef ? 'Payout account' : 'Payments',
    hint: isChef ? 'Bank account for weekly payouts' : 'Wallet, card & bank account',
    icon: CardIcon,
  },
  {
    group: isChef ? 'Kitchen setup' : undefined,
    to: '/verification',
    label: 'Kitchen setup',
    hint: 'Where you cook and your kitchen details',
    icon: SparkIcon,
    roles: ['chef'],
  },
  { group: 'Help & account', to: '/inbox', label: 'Inbox', hint: 'Order updates & chats', icon: ChatIcon },
  { group: 'Help & account', to: '/notifications', label: 'Sounds & notifications', hint: 'Your little moments of Chow', icon: SparkIcon },
  { group: 'Help & account', to: '/help', label: 'Help', hint: 'Support & order issues', icon: LifebuoyIcon },
  // Last and unstyled on purpose: findable (Play requires an in-app route, and
  // /delete-account tells people to look for "Profile → Delete account"), but
  // no louder than any other setting.
  { group: 'Help & account', to: '/account/delete', label: 'Delete account', hint: 'Close your account & remove your data', icon: UserIcon },
]

/** The menu-row icon tint, mixed off the `danger` token. */
const ROW_ICON_TINT = `color-mix(in srgb, ${minimalVars.color.danger} 8%, transparent)`

const Style = {
  Account: styled.div`
    display: flex;
    align-items: center;
    gap: ${minimalVars.space.sm};
  `,
  Avatar: styled.div`
    position: relative;
    width: 64px;
    height: 64px;
    border-radius: 18px;
    background: ${minimalVars.color.brandSoft};
    color: ${minimalVars.color.brand};
    display: grid;
    place-items: center;
    flex-shrink: 0;
  `,
  AvatarInner: styled.span`
    width: 100%;
    height: 100%;
    border-radius: inherit;
    overflow: hidden;
    display: grid;
    place-items: center;
  `,
  AvatarImg: styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
  `,
  /* Kept below metaSize on purpose. The badge fills a fixed 24px avatar button. */
  AvatarEdit: styled.button`
    position: absolute;
    right: -2px;
    bottom: -2px;
    width: 24px;
    height: 24px;
    padding: 0;
    appearance: none;
    -webkit-appearance: none;
    display: inline-grid;
    place-items: center;
    place-content: center;
    border: 1px solid ${minimalVars.color.bgSurface};
    border-radius: 10px;
    background: ${minimalVars.color.brand};
    color: ${minimalVars.color.textInverse};
    font-size: 11px;
    line-height: 1;
    cursor: pointer;
    box-shadow: none;
    transition: filter 160ms ease;

    > svg {
      display: block;
    }
    &:hover,
    &:focus-visible {
      filter: brightness(1.08);
      outline: none;
    }
    &:disabled {
      cursor: default;
      opacity: 0.7;
    }
  `,
  NameRow: styled.div`
    display: flex;
    align-items: center;
    gap: ${minimalVars.space.xs};
    flex-wrap: wrap;
    min-width: 0;
  `,
  EditLink: styled(MinimalButton)`
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 0;
    color: ${minimalVars.color.brand};
    font: inherit;
    font-size: ${minimalVars.typography.captionSize};
    font-weight: ${minimalVars.typography.weightSemibold};
  `,
  Name: styled.span`
    font-family: ${minimalVars.typography.displayFamily};
    font-weight: ${minimalVars.typography.weightSemibold};
    /* The display name stays at 1.15rem: h2Size is 22px and outranks the 13px Sub line. */
    font-size: 1.15rem;
    color: ${minimalVars.color.textPrimary};
  `,
  Sub: styled.span<{ $tone?: 'danger' }>`
    color: ${({ $tone }) => ($tone === 'danger' ? minimalVars.color.danger : minimalVars.color.textTertiary)};
    font-size: ${minimalVars.typography.captionSize};
  `,
  Dashboard: styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: ${minimalVars.space.sm};

    /* 860px matches no named stop. The rail needs this width for its 340px rail. */
    @media (min-width: 860px) {
      grid-template-columns: minmax(340px, 380px) minmax(0, 1fr);
      gap: ${minimalVars.space.md};
      align-items: start;
    }
  `,
  Sidebar: styled.div`
    display: grid;
    gap: ${minimalVars.space.sm};
  `,
  Main: styled.div`
    display: grid;
    gap: ${minimalVars.space.sm};
  `,
  Group: styled.section`
    h2 {
      margin: 0 0 6px;
      font-size: ${minimalVars.typography.captionSize};
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: ${minimalVars.color.textTertiary};
    }
    & + & { margin-top: 14px; }
  `,
  Menu: styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: ${minimalVars.space.xs};

    /* 600px matches no named stop. It keeps two-up rows off the phone layout. */
    @media (min-width: 600px) {
      grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
      gap: 10px;
    }
  `,
  Row: styled(Link)`
    display: grid;
    grid-template-columns: 36px minmax(0, 1fr) 16px;
    align-items: start;
    gap: 12px;
    min-height: 82px;
    padding: 14px;
    background: ${minimalVars.color.bgSurface};
    border: 1px solid ${minimalVars.color.borderSubtle};
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
    text-decoration: none;
    color: ${minimalVars.color.textPrimary};
    transition: background-color 160ms ease, border-color 160ms ease, transform 160ms ease;

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: ${minimalVars.color.bgElevated};
        border-color: ${minimalVars.color.brand};
        transform: translateY(-1px);
      }
    }
    &:focus-visible {
      outline: 2px solid ${minimalVars.color.borderFocus};
      outline-offset: 2px;
    }
  `,
  RowIcon: styled.span`
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: ${ROW_ICON_TINT};
    color: ${minimalVars.color.brand};
    display: grid;
    place-items: center;
    flex-shrink: 0;
    margin-top: 1px;
  `,
  RowCopy: styled.span`
    display: grid;
    gap: 3px;
    min-width: 0;
    align-content: start;
  `,
  RowLabel: styled.span`
    font-size: ${minimalVars.typography.bodySize};
    font-weight: 600;
    line-height: 1.25;
    color: ${minimalVars.color.textPrimary};
  `,
  RowHint: styled.span`
    color: ${minimalVars.color.textTertiary};
    font-size: ${minimalVars.typography.captionSize};
    line-height: 1.35;
  `,
  Chevron: styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${minimalVars.color.textTertiary};
    flex-shrink: 0;
    margin-top: ${minimalVars.space.xs};
  `,
  /* Unread count on a menu row. Order chat had no unread signal anywhere, so a
     message sat in the Inbox with nothing to say it was there. */
  RowCount: styled.span`
    flex-shrink: 0;
    min-width: 22px;
    padding: 2px 7px;
    border-radius: ${minimalVars.radius.pill};
    background: ${minimalVars.color.brand};
    color: ${minimalVars.color.textInverse};
    font-size: ${minimalVars.typography.metaSize};
    font-weight: ${minimalVars.typography.weightBold};
    text-align: center;
  `,
  SwitchLabel: styled.span`
    color: ${minimalVars.color.textTertiary};
    font-size: ${minimalVars.typography.metaSize};
    font-weight: ${minimalVars.typography.weightSemibold};
    letter-spacing: 0.1em;
    text-transform: uppercase;
  `,
  Switch: styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
  `,
  Chip: styled(MinimalButton)<{ $active: boolean }>`
    cursor: pointer;
    border: 1px solid ${({ $active }) => ($active ? minimalVars.color.brand : minimalVars.color.borderSubtle)};
    background: ${({ $active }) => ($active ? minimalVars.color.brandSoft : 'transparent')};
    color: ${({ $active }) => ($active ? minimalVars.color.brand : minimalVars.color.textSecondary)};
    border-radius: ${minimalVars.radius.pill};
    font: inherit;
    font-size: ${minimalVars.typography.captionSize};
    font-weight: ${minimalVars.typography.weightSemibold};
    padding: 6px 12px;
  `,
  AllergyHead: styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
    margin-bottom: ${minimalVars.space.xs};
  `,
  LocationRow: styled.div`
    display: flex;
    align-items: center;
    gap: ${minimalVars.space.xs};
    padding: ${minimalVars.space.xs} 0;
    border-bottom: 1px solid ${minimalVars.color.borderSubtle};
    &:last-of-type {
      border-bottom: 0;
    }
  `,
  LocationAction: styled(MinimalButton)<{ $danger?: boolean }>`
    background: none;
    border: 0;
    cursor: pointer;
    flex-shrink: 0;
    font: inherit;
    font-size: ${minimalVars.typography.captionSize};
    font-weight: ${minimalVars.typography.weightSemibold};
    color: ${({ $danger }) => ($danger ? minimalVars.color.textTertiary : minimalVars.color.brand)};
    padding: ${minimalVars.space["2xs"]};
    &:hover {
      color: ${({ $danger }) => ($danger ? minimalVars.color.danger : minimalVars.color.brand)};
    }
  `,
}

/**
 * Kitchen profile (chefs): the storefront's own identity — what it is called,
 * what it cooks, and where it cooks from.
 *
 * `cuisine` had no editor anywhere in the app. It is the line under a kitchen's
 * name on the landing page and the chefs strip, so every real kitchen showed up
 * with it blank while only the seeded ones read properly. `location_label` had
 * one indirect path (adding a primary kitchen location) and no direct one.
 *
 * The write sends only what this form owns. Verification, the open/closed sign,
 * and the avatar are all preserved by the service when omitted — a rule worth
 * relying on rather than working around by resending stale values, which is how
 * a photo upload used to blank a kitchen's description.
 */
const KitchenProfileCard = () => {
  const { profileId } = useChooseChow()
  const { user } = useAuth()
  const vendors = useVendorProfileSnapshot() as { records: readonly Record<string, unknown>[] }
  const vendor = useMemo(
    () => vendors.records.find((v) => String(v.profile_id ?? '') === profileId),
    [vendors.records, profileId],
  )

  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  const [cuisine, setCuisine] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const storedName = String(vendor?.display_name ?? user?.displayName ?? '')
  const storedCuisine = String(vendor?.cuisine ?? '')
  const storedArea = String(vendor?.location_label ?? '')

  const startEdit = () => {
    setName(storedName)
    setCuisine(storedCuisine)
    setError('')
    setEditing(true)
  }

  const save = async () => {
    const nextName = name.trim()
    if (!nextName) {
      setError('Your kitchen needs a name.')
      return
    }
    setBusy(true)
    setError('')
    try {
      if (isLiveRuntime()) {
        await commands.profile.setVendorProfile(profileId, {
          vendor_type: enumShort(vendor?.vendor_type, 'home_chef'),
          display_name: nextName,
          cuisine: cuisine.trim(),
        })
      }
      // Reflect immediately in the shared vendor store (live too — the command
      // above is awaited/confirmed). echoUpsert would no-op in live, leaving the
      // chef's own card and discovery showing the old name until a projection
      // sweep — the "saved but didn't update" class of bug.
      confirmedUpsert('profile.vendor_profiles', {
        ...(vendor ?? { profile_id: profileId }),
        display_name: nextName,
        cuisine: cuisine.trim(),
      })
      setEditing(false)
    } catch {
      setError('Could not save your kitchen details. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <MinimalCard padding="md" enter={false}>
      <Style.AllergyHead>
        <div>
          <Style.RowLabel as="h3" style={{ margin: 0 }}>
            Kitchen profile
          </Style.RowLabel>
          <Style.RowHint>What customers see above your dishes</Style.RowHint>
        </div>
        <Button size="sm" variant="secondary" onClick={() => (editing ? setEditing(false) : startEdit())}>
          {editing ? 'Cancel' : 'Edit'}
        </Button>
      </Style.AllergyHead>

      {editing ? (
        <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
          <MinimalInput
            label="Kitchen name"
            placeholder="e.g. Amara's Kitchen"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <MinimalInput
            label="Cuisine"
            placeholder="e.g. Nigerian & West African"
            value={cuisine}
            onChange={(e) => setCuisine(e.target.value)}
          />
          <Style.RowHint>
            Add or change where you cook under “Kitchen locations” — your primary
            location is the area customers see.
          </Style.RowHint>
          {error && <ErrorHint>{error}</ErrorHint>}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="sm" onClick={save} disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 4, marginTop: 8 }}>
          <Style.RowLabel>{storedName || 'Name your kitchen'}</Style.RowLabel>
          <Style.RowHint>
            {storedCuisine || 'No cuisine set — customers see “Home kitchen”'}
          </Style.RowHint>
          <Style.RowHint>{storedArea || 'No area yet. Add where you cook under Kitchen locations.'}</Style.RowHint>
        </div>
      )}
    </MinimalCard>
  )
}

/**
 * Kitchen locations (chefs): a vendor can cook from several places. One is
 * primary — it feeds discovery ("Nearby") and the single-label surfaces.
 * Dishes inherit their chef's locations, so keeping these accurate is what
 * makes dish/chef proximity communicative across the app.
 */
export const LocationsCard = () => {
  const { profileId } = useChooseChow()
  const locations = useVendorLocations(profileId)
  const { user } = useAuth()
  const live = isLiveRuntime() || !allowSeedContent() || Boolean(user?.id)
  const [address, setAddress] = useState('')
  const [editingId, setEditingId] = useState<string>()
  const [adding, setAdding] = useState(false)
  const [label, setLabel] = useState('')
  const [area, setArea] = useState('')
  const [city, setCity] = useState('')
  const [cityRef, setCityRef] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(false)

  const echo = () =>
    prototypeStoreRegistry.get('profile.vendor_locations') as unknown as
      | TenantProjectionStore<Record<string, unknown>>
      | undefined

  useEffect(() => {
    if (!live || !profileId) return
    let active = true
    void commands.profile.listVendorLocations(profileId).then((response) => {
      if (!active) return
      const rows = commandPayload(response).locations
      if (!Array.isArray(rows)) throw new Error('Kitchen locations could not be loaded.')
      const store = prototypeStoreRegistry.get('profile.vendor_locations') as unknown as TenantProjectionStore<Record<string, unknown>> | undefined
      for (const row of rows) store?.upsert(row)
    }).catch(() => { if (active) setErr(true) })
    return () => { active = false }
  }, [profileId, live])

  const add = async () => {
    if (!label.trim() || !address.trim() || !area.trim() || !city.trim() || busy) return
    setBusy(true)
    setErr(false)
    const payload = {
      label: label.trim(),
      address: address.trim(),
      location_id: editingId,
      area: area.trim(),
      city: city.trim(),
      // The picked catalogue city; the server canonicalizes `city` from it.
      // Empty only for a legacy location edited without re-picking, which the
      // server then files from the text.
      city_ref: cityRef,
      is_primary: editingId ? Boolean(locations.find((row) => row.id === editingId)?.isPrimary) : locations.length === 0,
    }
    try {
      let created: Record<string, unknown> = {
        id: `local-loc-${Date.now()}`,
        profile_id: profileId,
        ...payload,
      }
      if (live) {
        const saved = commandPayload(await commands.profile.setVendorLocation(profileId, payload))
        if (!saved.id) throw new Error('Location was not confirmed')
        created = saved
      }
      echo()?.upsert(created)
      setLabel('')
      setAddress('')
      setEditingId(undefined)
      setArea('')
      setCity('')
      setCityRef('')
      setAdding(false)
    } catch {
      setErr(true)
    } finally {
      setBusy(false)
    }
  }

  const makePrimary = async (id: string) => {
    const loc = locations.find((l) => l.id === id)
    if (!loc || busy) return
    setBusy(true)
    setErr(false)
    try {
      if (live) {
        await commands.profile.setVendorLocation(profileId, {
          location_id: loc.id,
          label: loc.label,
          area: loc.area,
          city: loc.city,
          // Re-sent so promoting a location never downgrades a picked city
          // to an inferred one.
          city_ref: loc.cityRef,
          address: loc.address,
          latitude: loc.latitude,
          longitude: loc.longitude,
          is_primary: true,
        })
      }
      const store = echo()
      for (const other of locations) {
        store?.upsert({
          id: other.id,
          profile_id: profileId,
          label: other.label,
          area: other.area,
          city: other.city,
          city_ref: other.cityRef,
          state: other.state,
          address: other.address,
          latitude: other.latitude,
          longitude: other.longitude,
          is_primary: other.id === id,
        })
      }
    } catch {
      setErr(true)
    } finally {
      setBusy(false)
    }
  }

  const remove = async (id: string) => {
    if (busy) return
    setBusy(true)
    setErr(false)
    try {
      if (live) await commands.profile.deleteVendorLocation(profileId, id)
      echo()?.remove(id)
    } catch {
      setErr(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <MinimalCard id="kitchen-locations" padding="md" enter={false}>
      <Style.AllergyHead>
        <div>
          <Style.RowLabel as="h3" style={{ margin: 0 }}>
            Kitchen locations
          </Style.RowLabel>
          <Style.RowHint>
            Customers collect their orders here. You need one before you can add dishes.
          </Style.RowHint>
        </div>
        <MinimalButton
          size="sm"
          variant="secondary"
          onClick={() => setAdding((v) => !v)}
          style={{ whiteSpace: 'nowrap' }}
        >
          {adding ? 'Close' : 'Add location'}
        </MinimalButton>
      </Style.AllergyHead>

      {locations.length === 0 && !adding && (
        <Style.RowHint>
          Add where you cook so people nearby can find you.
        </Style.RowHint>
      )}

      {err && <MinimalAlert tone="danger" title="Location not updated">Could not save your location change. Please try again.</MinimalAlert>}
      {locations.map((loc) => (
        <Style.LocationRow key={loc.id}>
          <PinIcon size={18} />
          <Style.RowCopy>
            <Style.RowLabel>{loc.label}</Style.RowLabel>
            <Style.RowHint>
              {locationLine(loc)}
              {loc.isPrimary ? ' · Primary' : ''}
            </Style.RowHint>
          </Style.RowCopy>
          <MinimalButton
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => {
              setEditingId(loc.id)
              setLabel(loc.label)
              setAddress(String(loc.address ?? ''))
              setArea(String(loc.area ?? ''))
              setCity(String(loc.city ?? ''))
              setCityRef(loc.cityRef)
              setAdding(true)
            }}
          >
            Edit location
          </MinimalButton>
          {!loc.isPrimary && (
            <Style.LocationAction variant="quiet" disabled={busy} onClick={() => makePrimary(loc.id)}>
              Make primary
            </Style.LocationAction>
          )}
          <Style.LocationAction
            variant="quiet"
            disabled={busy}
            $danger
            onClick={() => remove(loc.id)}
            aria-label={`Remove ${loc.label}`}
          >
            <CloseIcon size={14} />
          </Style.LocationAction>
        </Style.LocationRow>
      ))}

      {adding && (
        <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
          <MinimalInput
            label="Location name"
            required
            placeholder="e.g. Lekki kitchen"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <MinimalInput label="Street address" required hint="Customers come here to collect their food." value={address} onChange={(event) => setAddress(event.target.value)} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <MinimalInput
              label="Area"
              required
              placeholder="e.g. Lekki Phase 1"
              value={area}
              onChange={(e) => setArea(e.target.value)}
            />
            <CityPicker
              value={cityRef}
              hint={!cityRef && city ? `Saved as “${city}”. Choose it from the list to confirm.` : undefined}
              onChange={(picked) => {
                setCityRef(picked.ref)
                setCity(picked.name)
              }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <MinimalButton
              size="sm"
              variant="primary"
              onClick={add}
              disabled={busy || !label.trim() || !address.trim() || !area.trim() || !(cityRef || city.trim())}
            >
              {busy ? 'Saving…' : 'Save location'}
            </MinimalButton>
          </div>
        </div>
      )}
    </MinimalCard>
  )
}

/**
 * Identity card: the avatar (with photo upload) and display name (with inline
 * edit). Both persist through AuthContext.updateProfile → the profile domain.
 */
const IdentityCard = ({ role }: { role: ChowRole }) => {
  const { profileId } = useChooseChow()
  const { user, updateProfile } = useAuth()
  const vendors = useVendorProfileSnapshot() as { records: readonly Record<string, unknown>[] }
  const myVendor = useMemo(
    () => vendors.records.find((v) => String(v.profile_id ?? '') === profileId),
    [vendors.records, profileId],
  )

  const fileRef = useRef<HTMLInputElement>(null)
  const [editingName, setEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState('')
  const [busy, setBusy] = useState<'photo' | 'name' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [advisories, setAdvisories] = useState<ImageAdvisory[]>([])

  const onPickPhoto = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = '' // allow re-selecting the same file
    if (!file) return
    setBusy('photo')
    setError(null)
    setAdvisories([])
    try {
      if (role === 'chef') {
        const saved = await saveChefPhoto({
          file,
          profileId,
          fallbackOwner: user?.id ?? '',
          vendor: myVendor,
          displayName: user?.displayName ?? '',
          updateProfile,
        })
        setAdvisories(saved.advisories)
      } else {
        const uploaded = await uploadImage(file, 'profile', profileId || user?.id || 'me')
        await updateProfile({ avatarUrl: uploaded.url })
        setAdvisories(uploaded.advisories ?? [])
      }
    } catch (err) {
      // Surface the specific reason for size-ceiling rejections; fall back to a
      // generic message for transport failures.
      setError(
        err instanceof Error && /too large/i.test(err.message)
          ? err.message
          : 'Could not update photo. Please try again.',
      )
    } finally {
      setBusy(null)
    }
  }

  const startEditName = () => {
    setNameDraft(user?.displayName ?? '')
    setEditingName(true)
    setError(null)
  }

  const saveName = async () => {
    setBusy('name')
    setError(null)
    try {
      await updateProfile({ displayName: nameDraft })
      setEditingName(false)
    } catch {
      setError('Could not save your name. Please try again.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <MinimalCard padding="md" enter={false}>
      <Style.Account>
        <Style.Avatar>
          <Style.AvatarInner>
            {role === 'chef' ? (
              <ChowThumb src={user?.avatarUrl} alt={user?.displayName ?? 'Chef profile'}
                name={user?.displayName} ratio={[1, 1]} isChef />
            ) : user?.avatarUrl ? (
              <Style.AvatarImg
                src={mediaUrl(user.avatarUrl, 128)}
                srcSet={`${mediaUrl(user.avatarUrl, 128)} 1x, ${mediaUrl(user.avatarUrl, 384)} 3x`}
                alt={user?.displayName ?? 'Avatar'}
              />
            ) : (
              // Fill a healthy share of the 72px circle — a 24px glyph read as a
              // small, off-centre mark floating in the avatar.
              <UserIcon size={38} />
            )}
          </Style.AvatarInner>
          <Style.AvatarEdit
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy === 'photo'}
            aria-label="Change profile photo"
            title="Change profile photo"
          >
            {busy === 'photo' ? '…' : <CameraIcon size="sm" />}
          </Style.AvatarEdit>
          <input
            ref={fileRef}
            type="file"
            accept={IMAGE_ACCEPT}
            hidden
            onChange={onPickPhoto}
          />
        </Style.Avatar>
        <div>
          {editingName ? (
            <Style.NameRow>
              <MinimalInput
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                aria-label="Display name"
                autoFocus
              />
              <Style.EditLink variant="quiet" type="button" onClick={saveName} disabled={busy === 'name'}>
                {busy === 'name' ? 'Saving…' : 'Save'}
              </Style.EditLink>
              <Style.EditLink variant="quiet" type="button" onClick={() => setEditingName(false)}>
                Cancel
              </Style.EditLink>
            </Style.NameRow>
          ) : (
            <Style.NameRow>
              <Style.Name>{user?.displayName ?? 'Guest'}</Style.Name>
              {user && (
                <Style.EditLink variant="quiet" type="button" onClick={startEditName}>
                  Edit
                </Style.EditLink>
              )}
            </Style.NameRow>
          )}
          <Style.Sub>
            {role} · {user?.email || (user?.phone ? formatPhone(user.phone) : '')}
          </Style.Sub>
          {error && (
            <>
              <br />
              <Style.Sub role="alert" $tone="danger">
                {error}
              </Style.Sub>
            </>
          )}
          {busy !== 'photo' && <PhotoAdvice advisories={advisories} />}
        </div>
      </Style.Account>
    </MinimalCard>
  )
}

// A block, not an inline span: inline, a long error ran straight into the
// buttons beside and below it with no breathing room.
const ErrorHint = styled.p`
  margin: ${minimalVars.space.xs} 0;
  padding: ${minimalVars.space.xs} ${minimalVars.space.sm};
  border-radius: ${minimalVars.radius.sm};
  background: ${minimalVars.color.dangerSoft};
  color: ${minimalVars.color.danger};
  font-size: 0.8125rem;
  line-height: 1.45;
  overflow-wrap: anywhere;
`

/* ── Account deletion ─────────────────────────────────────────────────── */

const DangerCard = styled(MinimalCard)`
  && {
    border-color: ${() => `color-mix(in srgb, ${minimalVars.color.danger} 34%, transparent)`};
  }
`

const DangerHead = styled.h3`
  margin: 0 0 ${minimalVars.space["2xs"]};
  /* bodySize matches 0.95rem; captionSize is 13px and matches the copy beneath this head. */
  font-size: ${minimalVars.typography.bodySize};
  color: ${minimalVars.color.danger};
`

const DangerCopy = styled.p`
  margin: 0 0 12px;
  color: ${minimalVars.color.textSecondary};
  font-size: ${minimalVars.typography.captionSize};
  line-height: 1.55;
`

const DangerActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${minimalVars.space.xs};
  margin-top: 10px;
`

const ScheduledNote = styled.p`
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: ${minimalVars.radius.md};
  background: ${() => `color-mix(in srgb, ${minimalVars.color.danger} 10%, transparent)`};
  color: ${minimalVars.color.textPrimary};
  font-size: ${minimalVars.typography.captionSize};
  line-height: 1.55;
`

/**
 * Render a server timestamp as a date a person reads, not an ISO string.
 *
 * The server sends Postgres' own `timestamptz::text` rendering —
 * "2026-10-10 18:10:34.467274+00" — which is NOT valid ISO 8601 twice over: a
 * space where the T belongs, and a two-digit UTC offset where the standard
 * wants "+00:00" or "Z". `new Date()` rejects it outright in V8, so without
 * both repairs this fell through to printing the raw timestamp at the user, on
 * the one screen where the date is the entire message.
 */
const readableDate = (raw: string): string => {
  if (!raw) return ''
  const iso = raw.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00')
  const parsed = new Date(iso)
  return Number.isNaN(parsed.getTime())
    ? raw
    : parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
}

/**
 * Delete-my-account, in the app.
 *
 * Google Play requires this route to exist alongside the public one at
 * /delete-account for any app that creates accounts. It is the same command
 * either way; this is just the door a signed-in person finds first.
 *
 * Three things are deliberate:
 *
 * 1. Deletion is SCHEDULED, not immediate. The server opens a 30-day window and
 *    the account keeps working throughout it, so this card turns into a "we are
 *    going to delete this on <date>" notice with an undo. That window is the
 *    protection against a stolen session and against a decision made in anger.
 * 2. The confirmation is the account's own email, typed. Checked on the server
 *    too — this copy of the check is a speed bump, not the lock.
 * 3. Blockers are shown BEFORE the person commits, not after. Being told to
 *    withdraw a wallet balance only once you have typed your email out is a
 *    small cruelty the server's blocker report makes unnecessary.
 */
const DeleteAccountCard = () => {
  const { profileId } = useChooseChow()
  // No sign-out on success: the account keeps working for the whole grace
  // period, and throwing the person out would make a reversible decision feel
  // final — and hide the "keep my account" button that undoes it.
  const [open, setOpen] = useState(false)
  const [confirmation, setConfirmation] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [state, setState] = useState<{
    scheduled: boolean
    purgeAfter: string
    graceDays: number
    openOrders: number
    walletMinor: number
  }>({ scheduled: false, purgeAfter: '', graceDays: 30, openOrders: 0, walletMinor: 0 })

  const readState = (payload: Record<string, unknown>) => {
    const blockers = (payload.blockers ?? {}) as Record<string, unknown>
    setState({
      scheduled: Boolean(payload.scheduled),
      purgeAfter: String(payload.purge_after ?? ''),
      graceDays: Number(payload.grace_period_days ?? 30),
      openOrders: Number(blockers.open_orders ?? 0),
      walletMinor: Number(blockers.wallet_balance_minor ?? 0),
    })
  }

  useEffect(() => {
    if (!isLiveRuntime() || !profileId) return undefined
    let alive = true
    void commands.account
      .getDeletion(profileId)
      .then((res) => {
        if (alive) readState(commandPayload(res))
      })
      .catch(() => undefined)
    return () => {
      alive = false
    }
  }, [profileId])

  const blocked = state.openOrders > 0 || state.walletMinor > 0

  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      const res = await commands.account.requestDeletion(profileId, confirmation)
      readState(commandPayload(res))
      setOpen(false)
      setConfirmation('')
    } catch (cause) {
      // The server's message is the useful one — it names the blocker, or says
      // the confirmation did not match. Swallowing it and showing "something
      // went wrong" is what makes a refusal impossible to act on.
      setError(cause instanceof Error ? cause.message : 'Could not schedule deletion.')
    } finally {
      setBusy(false)
    }
  }

  const cancel = async () => {
    setBusy(true)
    setError('')
    try {
      await commands.account.cancelDeletion(profileId)
      setState((prev) => ({ ...prev, scheduled: false, purgeAfter: '' }))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not cancel deletion.')
    } finally {
      setBusy(false)
    }
  }

  if (state.scheduled) {
    return (
      <DangerCard padding="md" enter={false}>
        <DangerHead>Account scheduled for deletion</DangerHead>
        <ScheduledNote>
          This account and its data will be permanently deleted on{' '}
          <strong>{readableDate(state.purgeAfter)}</strong>. Until then everything keeps working, and
          you can change your mind.
        </ScheduledNote>
        {error && <ErrorHint role="alert">{error}</ErrorHint>}
        <Button variant="secondary" onClick={() => void cancel()} disabled={busy}>
          {busy ? 'Working…' : 'Keep my account'}
        </Button>
      </DangerCard>
    )
  }

  return (
    <DangerCard padding="md" enter={false}>
      <DangerHead>Delete account</DangerHead>
      <DangerCopy>
        Deleting removes your profile, saved addresses, dietary information and saved dishes. Order
        and payment records are kept where the law requires.{' '}
        <Link to="/delete-account">What is deleted and what is kept</Link>.
      </DangerCopy>

      {blocked && (
        <ScheduledNote>
          Before you can delete this account:{' '}
          {state.openOrders > 0 && <>finish or cancel your {state.openOrders} open order
            {state.openOrders === 1 ? '' : 's'}</>}
          {state.openOrders > 0 && state.walletMinor > 0 && ', and '}
          {state.walletMinor > 0 && <>withdraw your wallet balance</>}.
        </ScheduledNote>
      )}

      {!open ? (
        <Button variant="secondary" onClick={() => setOpen(true)} disabled={blocked}>
          Delete my account
        </Button>
      ) : (
        <>
          <DangerCopy>
            Your account will be deleted in <strong>{state.graceDays} days</strong>. It keeps working
            until then, and you can cancel any time before the date. To confirm, type the email
            address on this account.
          </DangerCopy>
          <MinimalInput
            label="Confirm your email"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            placeholder="you@example.com"
            autoComplete="off"
          />
          {error && <ErrorHint role="alert">{error}</ErrorHint>}
          <DangerActions>
            <Button variant="secondary" onClick={() => void submit()} disabled={busy || !confirmation}>
              {busy ? 'Scheduling…' : 'Schedule deletion'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setOpen(false)
                setConfirmation('')
                setError('')
              }}
              disabled={busy}
            >
              Cancel
            </Button>
          </DangerActions>
        </>
      )}
    </DangerCard>
  )
}

/** Profile → Delete account. Its own page so the profile itself stays calm. */
export const AccountDeletionPage = () => {
  const back = useBackNav()
  return (
    <Screen
      header={
        <PageHeader title="Delete account" accentDot onBack={back} />
      }
    >
      <DeleteAccountCard />
    </Screen>
  )
}

export const ProfilePage = () => {
  const { edition, setEdition } = useEdition()
  const { role, isChef, profileId } = useChooseChow()
  const unreadThreads = useUnreadThreadCount(profileId, role)
  const { logout } = useAuth()
  const navigate = useNavigate()



  const menu = buildMenu(isChef).filter((item) => !item.roles || item.roles.includes(role))

  const signOut = () => {
    logout()
    navigate('/', { replace: true })
  }

  return (
    <Screen
      header={
        <PageHeader
          title={isChef ? 'Chef profile' : 'Profile'}
          accentDot
        />
      }
    >
      <Style.Dashboard>
        <Style.Sidebar>
          <InstallAppNudge />
          <IdentityCard role={role} />

          {isChef && <KitchenProfileCard />}

          {isChef && <ShareKitchenCard />}

          {isChef && <LocationsCard />}

          <Button variant="secondary" onClick={signOut}>
            Sign out
          </Button>

          {allowSeedContent() && (
            <div>
              <Style.SwitchLabel>Switch product · dev</Style.SwitchLabel>
              <Style.Switch role="group" aria-label="Switch edition">
                {EDITIONS.map((option: Edition) => (
                  <Style.Chip variant="quiet"
                    key={option}
                    type="button"
                    $active={option === edition}
                    aria-pressed={option === edition}
                    onClick={() => setEdition(option)}
                  >
                    {option}
                  </Style.Chip>
                ))}
              </Style.Switch>
            </div>
          )}
        </Style.Sidebar>

        <Style.Main>
          {['Your food', isChef ? 'Earnings & payouts' : 'Payments', 'Help & account'].map((group) => {
            const billingGroup = isChef ? 'Earnings & payouts' : 'Payments'
            const items = menu.filter((item) => (item.group || billingGroup) === group)
            if (!items.length) return null
            return <Style.Group key={group} aria-label={group}><h2>{group}</h2><Style.Menu>
            {items.map((item) => {
              const Glyph = item.icon
              const unread = item.to === '/inbox' ? unreadThreads : 0
              return (
                <Style.Row key={item.to} to={item.to}>
                  <Style.RowIcon>
                    <Glyph size="md" />
                  </Style.RowIcon>
                  <Style.RowCopy>
                    <Style.RowLabel>{item.label}</Style.RowLabel>
                    <Style.RowHint>
                      {unread > 0
                        ? `${unread} conversation${unread === 1 ? '' : 's'} with new messages`
                        : item.hint}
                    </Style.RowHint>
                  </Style.RowCopy>
                  {unread > 0 && (
                    <Style.RowCount aria-label={`${unread} unread conversations`}>
                      {unread}
                    </Style.RowCount>
                  )}
                  <Style.Chevron>
                    <ChevronRightIcon size="sm" />
                  </Style.Chevron>
                </Style.Row>
              )
            })}
          </Style.Menu></Style.Group>
          })}
        </Style.Main>
      </Style.Dashboard>
    </Screen>
  )
}
